import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLocalizeMessage, Table, SearchField, SegmentedControl } from 'libs.nucleus';
import { useParticipantsTableColumns } from './participants_table_columns.hook';
import { EParticipantCategory, useFetchParticipants } from '../../hooks/use_fetch_participants';
const DEFAULT_PAGE_SIZE = 10;
const SEARCH_FIELD_WIDTH = 560;
export const ParticipantsTable = () => {
    const translate = useLocalizeMessage();
    const { participantsColumns } = useParticipantsTableColumns();
    const [searchInput, setSearchInput] = useState('');
    const { isFetching, participants, totalPages, pagination, setPagination, setSearchValue, setCategory: updateParticipantCategory, } = useFetchParticipants({
        initialPage: 0,
        initialPageSize: DEFAULT_PAGE_SIZE,
    });
    const [selectedCategory, setSelectedCategory] = useState(EParticipantCategory.ACTIVE);
    const categoryFilters = useMemo(() => [
        {
            id: EParticipantCategory.ACTIVE,
            label: translate('Active'),
        },
        {
            id: EParticipantCategory.DEACTIVATED,
            label: translate('Deactivated'),
        },
    ], [translate]);
    const handleSearch = useDebouncedCallback((nextValue) => {
        setSearchValue(nextValue);
        setPagination((previous) => (previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }));
    }, 500);
    const handleSearchClear = () => {
        handleSearch.cancel();
        setSearchInput('');
        setSearchValue(undefined);
        setPagination((previous) => ({ ...previous, pageIndex: 0 }));
    };
    const handleCategoryFilterChange = (item) => {
        setSelectedCategory(item.id);
        updateParticipantCategory(item.id);
        setPagination({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
    };
    const activeCategoryIndex = useMemo(() => {
        const activeIdx = categoryFilters.findIndex((filter) => filter.id === selectedCategory);
        return activeIdx;
    }, [selectedCategory]);
    return (_jsx(Table, { columns: participantsColumns, data: participants || [], isLoading: isFetching, labelNoResults: translate('info.participants_table.empty'), paginationNavigationScreenReaderLabel: translate('info.participants_table.navigation'), hasPagination: true, manualPagination: true, manualFiltering: true, useInternalSearch: false, initialPageSize: DEFAULT_PAGE_SIZE, pageCount: totalPages, manualPaginationState: pagination, onPaginationChange: setPagination, customHeaderContent: _jsxs("div", { className: 'flex w-full items-end gap-6', children: [_jsx(SegmentedControl, { activeItemIndex: activeCategoryIndex, items: categoryFilters, onItemClick: handleCategoryFilterChange }), _jsx("div", { style: { width: SEARCH_FIELD_WIDTH }, children: _jsx(SearchField, { ariaLabel: translate('generic.search'), autoComplete: 'off', dataTestId: 'nucleus-table--search-field', placeholder: translate('generic.search'), value: searchInput, width: 'full', onChange: (event) => {
                            setSearchInput(event.target.value);
                            handleSearch(event.target.value);
                        }, onClear: handleSearchClear, onSearch: (value) => {
                            setSearchInput(value ?? '');
                            handleSearch(value);
                        } }) })] }) }));
};
